
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
} from '@/components/ui/chart';
import { ClicksByProduct } from '@/hooks/product/useProductAnalytics';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface TopProductsChartProps {
  data: ClicksByProduct[];
}

// Define proper type for the CustomTooltip props
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: any;
}

export function TopProductsChart({ data }: TopProductsChartProps) {
  // Define chart colors configuration
  const chartConfig = {
    clicks: {
      label: 'Current Period',
      color: '#82ca9d'
    },
    previous_clicks: {
      label: 'Previous Period',
      color: '#8884d8'
    }
  };

  // Check if comparison data exists
  const hasComparisonData = data.some(item => item.previous_clicks !== undefined);

  // Create custom tooltip to show growth indicators
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const productData = payload[0].payload;
      return (
        <div className="bg-background border p-3 rounded-md shadow-md">
          <p className="font-semibold">{productData.product_name}</p>
          <p>Current: {productData.clicks} clicks</p>
          {productData.previous_clicks !== undefined && (
            <p>Previous: {productData.previous_clicks} clicks</p>
          )}
          {productData.growth !== undefined && (
            <div className="flex items-center text-sm mt-1">
              {productData.growth >= 0 ? (
                <ArrowUp className="h-3 w-3 text-emerald-500 mr-1" />
              ) : (
                <ArrowDown className="h-3 w-3 text-rose-500 mr-1" />
              )}
              <span className={productData.growth >= 0 ? "text-emerald-500" : "text-rose-500"}>
                {Math.abs(productData.growth)}%
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Products</CardTitle>
        <CardDescription>Compare performance of most clicked products</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          {data.length > 0 ? (
            <ChartContainer config={chartConfig}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis 
                  dataKey="product_name" 
                  type="category" 
                  width={150}
                  tickLine={true}
                  axisLine={true}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="clicks" 
                  radius={[0, 4, 4, 0]}
                  fill={chartConfig.clicks.color}
                />
                {hasComparisonData && (
                  <Bar 
                    dataKey="previous_clicks" 
                    radius={[0, 4, 4, 0]}
                    fill={chartConfig.previous_clicks.color}
                    fillOpacity={0.7}
                  />
                )}
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">No product click data available for this period</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
